import c from 'classnames';
import { stringToUrl, userValueFormat } from 'helpers';
import React from 'react';
import CountUp from 'react-countup';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { setModalOpened } from 'store/modal';

import { Button, ButtonColors, Typography } from '@repo/ui';
import { Markdown } from 'components/Markdown/Markdown';
import { useAnalytics } from 'contexts/analytics/AnalyticsProvider';
import { useCurrentKitAndTinyAccount } from 'hooks/useCurrentKitAndTinyAccount';
import { useIsFirstModalView } from 'hooks/useIsFirstModalView';
import { RangesGraph } from 'views/Results/Insights/RangesGraph/RangesGraph';
import { EvaluationPillFromColor } from 'views/Results/Insights/SubMetricGraphs/SubMetricGraphs';
import { MainFinding } from 'views/Results/Summary/SummaryCards/NoteCard/useMainFindings';
import { SLIDE_BACKGROUND_COLORS } from '../slideBackgroundColors';

import { AgeGroup, getAgeGroup } from '@repo/utils';
import slideStyles from './Slides.module.scss';
import styles from './TopThreeItem.module.scss';

interface TopThreeItemProps {
  metric: MainFinding;
  count?: number;
}

const getDecimalPlacesFromValue = (value: number): number => {
  const valueStr = value.toString();
  if (!valueStr.includes('.')) return 0;
  return valueStr.split('.')[1].length;
};

export const TopThreeItem: React.FC<TopThreeItemProps> = ({
  metric,
  count,
}) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const dispatch = useDispatch();
  const { trackEvent } = useAnalytics();
  const { currentKit, currentKitId, currentTinyAccount } =
    useCurrentKitAndTinyAccount();
  const isFirstView = useIsFirstModalView('firstLook');
  const isSpecies = metric.gvh?.taxa_level === 'species';
  const isAdult =
    getAgeGroup(currentKit?.age_at_sampling ?? 0) === AgeGroup.ADULT;

  const modalIdentifier = {
    name: 'firstLook',
    props: {},
  };

  const dismissModal = () => {
    dispatch(setModalOpened(modalIdentifier));
  };

  const handleViewDetails = () => {
    trackEvent('First Look', {
      event_type: 'button_clicked',
      button_text: 'View Details',
      slide_id: `top-three-${metric.gvh?.name}`,
      action: 'view_details',
      metric_name: metric.gvh?.name,
    });
    dismissModal();
    if (currentKitId && metric.sample_health_id && metric.gvh) {
      navigate(
        `/results/${currentKitId}/insights/${stringToUrl(
          metric.gvh.kic?.kis?.display_title ?? '',
        )}/${metric.sample_health_id}`,
        { state: { from: pathname } },
      );
    }
  };

  return (
    <div
      className={c(slideStyles.slideWrapper, styles.slideContainer)}
      style={{ backgroundColor: SLIDE_BACKGROUND_COLORS.topThreeItem }}
    >
      <div className={styles.contentContainer}>
        <div className={styles.top}>
          {count && (
            <Typography variant='body-s'>
              Top {count} metrics to address
            </Typography>
          )}
          <Typography variant='heading-2xl' italic={isSpecies}>
            {metric.gvh?.name}
          </Typography>
          <Typography variant='body-m'>
            {isAdult ? 'Your' : `${currentTinyAccount?.first_name}'s`} value is
          </Typography>
          <Typography
            variant='heading-3xl'
            className={c(styles.value, styles[metric.evaluation_color])}
          >
            {(() => {
              const formattedValue = userValueFormat(
                metric.user_value,
                metric.gvh?.data_type || 'number',
                metric.user_string,
              );

              // If it's a string type, return as-is
              if (metric.gvh?.data_type === 'string') {
                return formattedValue;
              }

              // Parse the formatted value to extract number and symbol
              const hasPercent = formattedValue.endsWith('%');
              const numericString = hasPercent
                ? formattedValue.slice(0, -1)
                : formattedValue;
              const numericValue = parseFloat(numericString);

              // Determine decimal places from the formatted string
              const decimalPlaces = numericString.includes('.')
                ? numericString.split('.')[1].length
                : 0;

              return (
                <>
                  <CountUp
                    end={numericValue}
                    duration={1.5}
                    decimals={decimalPlaces}
                    separator=','
                  />
                  {hasPercent && '%'}
                </>
              );
            })()}
          </Typography>

          <div className={styles.pillContainer}>
            <EvaluationPillFromColor
              evaluation_color={metric.evaluation_color}
            />
          </div>
          {metric.gvh?.data_type !== 'string' &&
            metric.gvh?.range_bar_display && (
              <div className={styles.rangesGraphWrapper}>
                <RangesGraph
                  dataType={metric.gvh?.data_type || 'number'}
                  relativeAbundance={metric.user_value}
                  ranges={metric.gvh?.gvhkr ?? []}
                  scaleBarMin={metric.gvh?.scale_bar_min}
                  scaleBarMax={metric.gvh?.scale_bar_max}
                  evaluationColor={metric.evaluation_color}
                />
              </div>
            )}
          {metric.gvh?.short_description && (
            <Markdown className={styles.shortDescription}>
              {metric.gvh?.short_description}
            </Markdown>
          )}

          <Typography variant='label'>
            View the detail page of this metric to learn more.
          </Typography>
        </div>

        {!isFirstView && (
          <div className={styles.buttonsContainer}>
            <Button
              variant='outline'
              color={ButtonColors.PURPLE}
              onClick={handleViewDetails}
            >
              View Details
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
